/* eslint-disable @angular-eslint/component-selector */
import { Component, Inject, Input, LOCALE_ID, OnInit, ViewEncapsulation, HostListener,
ViewChild, TemplateRef, ChangeDetectorRef, OnChanges, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AuthConfig, NullValidationHandler, OAuthService } from 'angular-oauth2-oidc';
import { ConfigService } from './shared/services/config/config.service';
import { ActivationStart, Router, RouterOutlet, RoutesRecognized } from '@angular/router';
import { SettingsService } from './shared/services/settings/settings.service';
import { filter, first, takeUntil } from 'rxjs/operators';
import { UltiProAuthService } from './auth/ultiproauth.service';
import { IFrameUtils } from './iframes';
import { BaseObserver } from './shared/baseobserver';
import { HostLocationService } from './elements-routing/host-location.service';
import { ES5ZoneAwareRouter } from './elements-routing/es5ZoneAwareRouter';
import { SsgPopoverService } from './services/ssg-popover.service';
import { UkgPopover } from '@ignite/angular';
import { CustomerNotificationCategory } from './shared/enums/smarthub-enums';
import { HeaderService } from './components/header/header.service';

@Component({
  selector: 'ee-pay',
  templateUrl: './employee-pay-app.component.html',
  styleUrls: ['./employee-pay-app.component.less'],
  encapsulation: ViewEncapsulation.None
})
export class EmployeePayAppComponent extends BaseObserver implements OnInit, OnDestroy {
  private _psgInitialized = false;
  title = 'Payment Services Gateway';
  accessibilityPageTitle = this.title;
  popoverText: string;
  @ViewChild(UkgPopover, { static: false }) popover: UkgPopover;
  @ViewChild(RouterOutlet, { static: false }) outlet: RouterOutlet;

  @Input() set psgInitialized(value: boolean) {
    this._psgInitialized = value;
    if (value) {
      this.settingsService.SettingsInitialized.next();
    }
  }

  get psgInitialized() {
    return this._psgInitialized;
  }

  constructor(
    @Inject(LOCALE_ID) protected localeId: string,
    private readonly translateService: TranslateService,
    private readonly oauthService: OAuthService,
    private readonly configService: ConfigService,
    private readonly settingsService: SettingsService,
    private readonly authService: UltiProAuthService,
    private readonly router: Router,
    private readonly hostLocationService: HostLocationService,
    private readonly es5Router: ES5ZoneAwareRouter,
    private readonly cdr: ChangeDetectorRef,
    private readonly popoverService: SsgPopoverService,
    private readonly headerService: HeaderService
  ) {
    super();
    this.translateService.use(localeId);
    // initialNavigationSubscription
    this.router.events
      .pipe(
        filter(event => event instanceof RoutesRecognized),
        first(),
        takeUntil(this.destroy$)
      )
      .subscribe((ev) => {
        if (!this.psgInitialized) {
          const recognized = ev as RoutesRecognized;
          this.parseSettingsHash(recognized.state.root.fragment);
        }
        this.configure();
      });
    /**
     * https://github.com/angular/angular/pull/20712#issuecomment-398351773
     */
    this.router.events
      .pipe(
        filter(event => event instanceof ActivationStart),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        // if another router-outlet is added to eepay, you'll need to filter on the outlet here.
        if (this.outlet) {
          this.outlet.deactivate();
        }
      });

    this.popoverService.showText.subscribe((value) => {
        this.popoverText = value.text;
        this.popover.present(value.event);
    });
  }

  /** 'navHome' event requests navigation to default route (e.g. dashboard). */
  @HostListener('navHome')
  onNavHome() {
    this.es5Router.navigate(['']);
  }

  @HostListener('deeplinkIntoAlertDetails', ['$event'])
  deeplinkIntoAlertDetails(event: CustomEvent) {

    const category: CustomerNotificationCategory = event.detail?.category;
    if(category){
      const headersOverride = {
        breadcrumb: 'ALERTS.ALERTS_BREADCRUMB',
      };
      this.headerService.navigate([{ outlets: { eepay: ['alertsdetails', { category }] } }], headersOverride);
    }
  }

  parseSettingsHash(fragment: string) {
    const settings = IFrameUtils.getSettingsFromFragment(fragment);

    if (fragment) {
      if (settings) {
        this.settingsService.tenantId = settings.tenantId;
        this.settingsService.ultiproUrl = settings.ultiproUrl;
        this.settingsService.ultiproCountry = settings.ultiproCountry;
        this.settingsService.siteId = settings.siteId;
        this.settingsService.alias = settings.alias;
        this.settingsService.loginToken = settings.loginToken;
        this.settingsService.userId = settings.userId || '';
        this.settingsService.arNumber = settings.arNumber || '';
        this.settingsService.locale = settings.locale;
        this.settingsService.email = settings.email;
      }
      // This tells Pendo it's OK to initialize
      this.settingsService.SettingsInitialized.next();
    }
  }

  private async configure() {
    const realmUrl = `${this.configService.identityBaseUrl}/oauth2/t/${this.settingsService.alias}`;
    const authConfig: AuthConfig = {
      clientId: this.configService.identityClientId,
      scope: this.configService.identityScope,
      skipIssuerCheck: true,
      oidc: true,
      redirectUri: this.configService.identityRedirectUri,
      postLogoutRedirectUri: this.configService.identityLogoutRedirectUri,
      silentRefreshRedirectUri: window.location.origin + '/silent-refresh.html',
      requestAccessToken: true,
      showDebugInformation: true,
      issuer: realmUrl,
      loginUrl: `${realmUrl}/authorize`,
      logoutUrl: `${realmUrl}/connect/endSession`,
      userinfoEndpoint: `${realmUrl}/userinfo`,
      tokenEndpoint: `${realmUrl}/access_token`,
      sessionCheckIFrameUrl: `${realmUrl}/connect/checkSession`,
      sessionChecksEnabled: false,
      /* responseType: this.configService.identityResponseType,
      jwksUri: `${realmUrl}/connect/jwk_uri`*/
    };

    if (!this.configService.identityEnabled) {
      this.authService.authorize(btoa(this.settingsService.tenantId + ':' + this.settingsService.loginToken));
    }

    this.oauthService.configure(authConfig);

    this.oauthService.tokenValidationHandler = new NullValidationHandler();
    this.oauthService.setStorage(sessionStorage);
  }

  ngOnInit() {
    const element = document.getElementById('pre-angular-spinner-overlay');

    if (element) {
      element.style.display = 'none';
    }

    this.hostLocationService.handleNavigation();
    this.router.initialNavigation();
  }

  ngOnDestroy(): void {
    this.hostLocationService.destroy();
  }
}
